import { React, useState, useEffect } from "react"
import { Card, CardContent } from "../../components/ui/card"
import { Button } from "../../components/ui/button"
import { Eye, Check } from "lucide-react"
import { JobModal } from "../Marketplace/Jobs/JobModal"
import { useJobApplications } from "../../hooks/DataFetching/useJobApplications"
import { useJobDetails } from "../../hooks/DataFetching/useJobDetails"
import { useUpdateApplicationStatus } from "../../hooks/DataFetching/useUpdateApplicationStatus"

const JobApplications = () => {
  const { applications, loading: applicationsLoading, error: applicationsError, refreshApplications } = useJobApplications()
  const { jobDetails, loading: jobDetailsLoading, error: jobDetailsError, fetchJobDetails } = useJobDetails()
  const { updateApplicationStatus, updateLoading, updateError } = useUpdateApplicationStatus()
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [notification, setNotification] = useState(null)

  const handleViewJob = async (application) => {
    setSelectedApplication(application)
    await fetchJobDetails(application.positionId)
  }

  const handleAcceptPosition = async (application) => {
    const success = await updateApplicationStatus(application.id, "Accepted")
    if (success) {
      setNotification({
        type: "success",
        message: `You have successfully accepted the position: ${application.positionTitle}`,
      })
      await refreshApplications()
    } else {
      setNotification({
        type: "error",
        message: updateError || "Failed to accept the position. Please try again.",
      })
    }
  }

  useEffect(() => {
    if (notification) {
      const timer = setTimeout(() => setNotification(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [notification])

  const renderApplications = (status) => {
    return applications
      .filter((app) => app.status === status)
      .map((app) => (
        <Card key={app.id} className="mb-2">
          <CardContent className="flex items-center justify-between p-4">
            <div>
              <h4 className="font-semibold text-primary">{app.positionTitle}</h4>
              <span className="text-sm text-muted-foreground">
                Applied: {new Date(app.applicationDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => handleViewJob(app)}>
                <Eye className="h-4 w-4 mr-2" />
                View
              </Button>
              {status === "Offered" && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => handleAcceptPosition(app)}
                  className="bg-green-500 hover:bg-green-600 text-white"
                  disabled={updateLoading}
                >
                  <Check className="h-4 w-4 mr-2" />
                  {updateLoading ? "Accepting..." : "Accept Position!"}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))
  }

  if (applicationsLoading) return <div>Loading applications...</div>
  if (applicationsError) return <div>Error loading applications: {applicationsError}</div>

  return (
    <div>
      {notification && (
        <div
          className={`p-4 mb-4 rounded ${notification.type === "success" ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}
        >
          {notification.message}
        </div>
      )}
      <h3 className="font-semibold mb-2">Pending/Submitted</h3>
      <div className="mb-4">{renderApplications("Pending")}</div>
      <h3 className="font-semibold mb-2">Offered</h3>
      <div className="mb-4">{renderApplications("Offered")}</div>
      <h3 className="font-semibold mb-2">Accepted</h3>
      <div className="mb-4">{renderApplications("Accepted")}</div>
      <h3 className="font-semibold mb-2">Rejected</h3>
      <div>{renderApplications("Rejected")}</div>
      {selectedApplication && jobDetails && (
        <JobModal
          job={{ ...jobDetails, application: selectedApplication }}
          isOpen={!!jobDetails}
          onClose={() => {
            setSelectedApplication(null)
            fetchJobDetails(null)
          }}
          hasApplied={true}
        />
      )}
      {jobDetailsLoading && <div>Loading job details...</div>}
      {jobDetailsError && <div>Error loading job details: {jobDetailsError}</div>}
    </div>
  )
}

export default JobApplications

